export type WhatsAppTemplateKey =
  | "first_contact"
  | "inquiry_reply"
  | "sample_confirmation"
  | "sample_shipped"
  | "sample_feedback"
  | "quotation_follow_up"
  | "payment_reminder"
  | "long_no_reply";

export interface WhatsAppTemplateContext {
  customerName?: string | null;
  companyName?: string | null;
  productName?: string | null;
  ownerName?: string | null;
}

export interface WhatsAppTarget {
  number: string | null;
  displayNumber: string | null;
  href: string | null;
  error: string | null;
}

export interface WhatsAppTemplateDefinition {
  key: WhatsAppTemplateKey;
  label: string;
  description: string;
  message: string;
}

export const whatsappTemplates: WhatsAppTemplateDefinition[] = [
  { key: "first_contact", label: "首次联系", description: "首次通过 WhatsApp 联系新询盘", message: "Hello {name}, this is {owner}. Thank you for your interest in {product}. I would be glad to learn more about your requirements and help with the next steps." },
  { key: "inquiry_reply", label: "询盘回复", description: "确认需求并推进产品信息", message: "Hello {name}, thank you for your inquiry about {product}. Could you please share your target quantity, market and preferred packaging? I will prepare the most relevant information for {company}." },
  { key: "sample_confirmation", label: "样品确认", description: "确认样品规格和寄送信息", message: "Hello {name}, we are ready to confirm the sample details for {product}. Please check the product specifications, delivery address and contact number so we can arrange the sample correctly." },
  { key: "sample_shipped", label: "样品已寄出", description: "通知物流单号和签收安排", message: "Hello {name}, your sample for {product} has been shipped. I will share the tracking details with you and follow up after delivery. Please let me know once it arrives safely." },
  { key: "sample_feedback", label: "询问样品结果", description: "跟进试用反馈和下一步", message: "Hello {name}, I hope the {product} sample arrived safely. Have you had a chance to test it? Your feedback will help us confirm the formula and prepare the next step." },
  { key: "quotation_follow_up", label: "报价跟进", description: "确认报价意见和待解决问题", message: "Hello {name}, I am following up on our quotation for {product}. Please let me know if you have any questions about price, MOQ, packaging or delivery time." },
  { key: "payment_reminder", label: "付款提醒", description: "礼貌提醒付款和订单安排", message: "Hello {name}, this is a friendly reminder regarding the pending payment for {product}. Once payment is confirmed, we can keep the production and delivery schedule moving." },
  { key: "long_no_reply", label: "长期未回复", description: "重新唤醒较久未回复的客户", message: "Hello {name}, I hope you are doing well. I wanted to check whether {product} is still part of your current plan. I am happy to update the proposal or help with any new requirements." },
];

function firstUseful(...values: Array<string | null | undefined>) {
  return values.map((value) => value?.trim()).find(Boolean) ?? "there";
}

export function renderWhatsAppTemplate(key: WhatsAppTemplateKey, context: WhatsAppTemplateContext = {}) {
  const template = whatsappTemplates.find((item) => item.key === key) ?? whatsappTemplates[0];
  return renderWhatsAppTemplateText(template.message, context);
}

export function renderWhatsAppTemplateText(message: string, context: WhatsAppTemplateContext = {}) {
  const customer = firstUseful(context.customerName, context.companyName);
  const company = firstUseful(context.companyName, "your company");
  const product = firstUseful(context.productName, "the products you are interested in");
  const owner = firstUseful(context.ownerName, "the YKY Hair Care team");
  return message.replace(/\{(name|company|product|owner)\}/g, (_, token: string) => ({ name: customer, company, product, owner })[token] ?? "");
}

export function normalizeWhatsAppNumber(rawPhone: string | null | undefined): WhatsAppTarget {
  const raw = rawPhone?.trim().replace(/^＋/, "+") ?? "";
  if (!raw) return { number: null, displayNumber: null, href: null, error: "客户没有 WhatsApp 号码" };
  if (!/^[+\d\s().-]+$/.test(raw)) return { number: null, displayNumber: null, href: null, error: "号码格式需要确认" };
  const international = raw.startsWith("+") ? raw : raw.startsWith("00") ? `+${raw.slice(2)}` : null;
  if (!international || international.slice(1).includes("+")) return { number: null, displayNumber: null, href: null, error: "号码格式需要确认" };
  const digits = international.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15 || digits.startsWith("0")) return { number: null, displayNumber: null, href: null, error: "号码格式需要确认" };
  return { number: digits, displayNumber: `+${digits}`, href: `https://wa.me/${digits}`, error: null };
}

export function resolveWhatsAppTarget(rawPhone: string | null | undefined, message = ""): WhatsAppTarget {
  const target = normalizeWhatsAppNumber(rawPhone);
  if (!target.href) return target;
  const query = message.trim() ? `?text=${encodeURIComponent(message.trim())}` : "";
  return { ...target, href: `${target.href}${query}` };
}
